import CircularProgress from '@material-ui/core/CircularProgress';
import classNames from 'classnames';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import * as React from 'react';
import { Nullable } from '../Shared/Types';
import { iconLight } from '../Styles/Colours';
import styles from './AutosaveIndicator.module.scss';
import { CloudDoneIcon, CloudErrorIcon } from './Icons';

export enum AutosaveStates {
    initial = 'initial',
    saving = 'saving',
    saved = 'saved',
    failed = 'failed'
}

interface Props {
    textColor?: string;
    saveState: AutosaveStates;
    retryFunction?: Nullable<() => Promise<void>>;
}

export const AutosaveIndicator: React.FC<Props> = props => {
    const [saveTime, setSaveTime] = useState(props.saveState === AutosaveStates.saved ? new Date() : null);
    const [saveTimeInMinutes, setSaveTimeInMinutes] = useState(0);

    const updateSaveTime = () => {
        if (props.saveState === AutosaveStates.saved) {
            setSaveTimeInMinutes(!!saveTime ? moment().diff(saveTime, 'minutes') : 0);
        } else {
            setSaveTimeInMinutes(0);
        }
    };

    useEffect(() => {
        let timeOut: NodeJS.Timeout;
        if (props.saveState === AutosaveStates.saved) {
            setSaveTime(new Date());
            updateSaveTime();

            timeOut = setInterval(() => {
                updateSaveTime();
            }, 3000);
        } else {
            setSaveTime(null);
        }

        return () => {
            if (timeOut) clearInterval(timeOut);
        };
    }, [props.saveState]);

    const retryClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (props.retryFunction) props.retryFunction();
    };

    const autosave = classNames(styles.autosave, {
        [styles.saving]: props.saveState === AutosaveStates.saving
    });

    const autosaveText = classNames(styles.autosaveText, {
        [styles.saving]: props.saveState === AutosaveStates.saving,
        [styles.saveFailed]: props.saveState === AutosaveStates.failed
    });

    const autosaveSpinner = classNames(styles.autosaveSpinner, {
        [styles.saving]: props.saveState === AutosaveStates.saving,
        [styles.saveFailed]: props.saveState === AutosaveStates.failed
    });

    const autosaveError = classNames(styles.autosaveError, {
        [styles.saveFailed]: props.saveState === AutosaveStates.failed
    });

    return (
        <div className={autosave}>
            <span className={autosaveText} style={{ color: props.textColor }}>
                <CloudDoneIcon noPadding size={'16'} color={iconLight} />
                <span className={styles.spinnerText}>
                    {props.saveState === AutosaveStates.saved && saveTimeInMinutes > 0
                        ? `Last edited ${saveTimeInMinutes} minute${saveTimeInMinutes > 1 ? 's' : ''} ago`
                        : 'Changes saved'}
                </span>
            </span>
            <span className={autosaveSpinner}>
                <CircularProgress size={16} />
                <span className={styles.spinnerText} style={{ color: props.textColor }}>
                    Saving...{' '}
                </span>
            </span>
            <span className={autosaveError}>
                <CloudErrorIcon size='16' color={iconLight} />
                <span className={styles.spinnerText}>Error Saving.</span>
                <span className={styles.retryText} onClick={retryClick}>
                    Retry
                </span>
            </span>
        </div>
    );
};
